import { getFilterSelector } from '@/pages/dashboard/components/NewFilter/util';
import type {
  FilterItemType,
  PageFiltersType,
} from '@/pages/dashboard/types/dashboard';

type TDashboardSelectorGameConfig = {
  default_selector?: PageFiltersType[];
  page_selector?: PageFiltersType[];
};

/**
 * 与 page_selector 精确 path 匹配 + getFilterSelector 多段路径兜底
 */
export const resolvePageSelectorFilters = (
  pageSelector: PageFiltersType[] | undefined,
  path: string,
): FilterItemType[] | undefined => {
  if (!pageSelector?.length) {
    return undefined;
  }
  const pathNorm = `/${String(path).replace(/^\//, '')}`
    .trim()
    .toLowerCase();
  let row = pageSelector.find(
    item =>
      String(item?.path || '')
        .trim()
        .toLowerCase() === pathNorm,
  );
  row = getFilterSelector(pageSelector, row, pathNorm);
  return row?.filters;
};

export const pickFilterViewType = (row: {
  view_type?: string;
  viewType?: string;
}): string | undefined => row?.view_type || row?.viewType;

/**
 * gameConfig.default_selector 中 path 为 default 的全局筛选项（多数游戏的 view_type 配在这里）
 */
export const resolveDefaultSelectorFilters = (
  defaultSelector: PageFiltersType[] | undefined,
): FilterItemType[] => {
  const row = defaultSelector?.find(
    item => String(item?.path || '').trim().toLowerCase() === 'default',
  );
  return row?.filters || [];
};

const mergeFilterRowsByKey = (
  base: FilterItemType[],
  overlay: FilterItemType[],
): FilterItemType[] => {
  if (!overlay?.length) {
    return base || [];
  }
  if (!base?.length) {
    return overlay || [];
  }
  const byKey = new Map<string, FilterItemType>(
    base.map(x => [x.key, { ...x }]),
  );
  overlay.forEach(x => {
    if (!x?.key) {
      return;
    }
    const prev = byKey.get(x.key);
    const merged: FilterItemType = prev ? { ...prev, ...x } : x;
    byKey.set(x.key, merged);
  });
  return base.map(x => byKey.get(x.key) || x);
};

/**
 * 先取 default_selector.default，再按 path 合并 page_selector（同 key 以页面配置覆盖）
 * 解决仅配全局 default、未单独配页面 path 时 view_type 不生效的问题
 */
export const resolveDashboardSelectorFilters = (
  gameConfig: TDashboardSelectorGameConfig | undefined,
  path: string,
): FilterItemType[] => {
  const defaultRows = resolveDefaultSelectorFilters(gameConfig?.default_selector);
  const pageRows = resolvePageSelectorFilters(gameConfig?.page_selector, path) || [];
  return mergeFilterRowsByKey(defaultRows, pageRows);
};
